import {
  adminMotorVars,
  defaultMotorVars,
  deleteMotors,
  ecopetrolMotorVars,
  kocMotorVars,
  restoreMotors
} from "@/models/equipment/hps_motors/predicates";
import type { Motors, Motors_Bool_Exp } from "gql/api";
import {
  useDeleteMotorsMutation,
  useInsertMotorMutation,
  useHpsMotorsQuery,
  useRestoreMotorsMutation,
  useUpdateMotorByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import type { ComputedRef, Ref } from "vue";
import { computed, ref } from "vue";
import { useEquipment } from "../baseEquipment";
import type { UseEquipment } from "../equipmentTypes";
import type { HpsMotorRowData } from "./hpsMotorTableColumns";
import { HpsMotorTableColumns } from "./hpsMotorTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";
import { get50HzNameplate } from "@/libs/calculations/motors";

export interface UseHpsMotorsOptions {
  /**
   * Target 60Hz nameplate HP to filter/sort around.
   */
  requiredHP?: number;
  /**
   * When true, apply HP-based filtering and sorting.
   */
  enableHPFilter?: boolean;
}

export interface UseHpsMotors extends UseEquipment<Motors> {
  hpFilter: Ref<boolean>;
  targetHP: Ref<number>;
  fetching: Ref<boolean>;
}

export function HpsMotors(
  isAdmin: boolean,
  options: UseHpsMotorsOptions = {}
): UseHpsMotors {
  const baseEquipment = useEquipment<Motors>(isAdmin);
  const title = "Hps Motors";
  const user = useUserInfo();

  // Reactive state for HP-based filtering, similar to pumps flow rate filter
  const hpFilter = ref(options.enableHPFilter ?? false);
  const targetHP = ref(options.requiredHP ?? 0);

  const getVariables = () => {
    const baseVars = (() => {
      if (user.is_koc_customer) return kocMotorVars;
      if (user.is_ecopetrol_customer) return ecopetrolMotorVars;
      if (user.is_admin) return adminMotorVars;
      return defaultMotorVars;
    })();

    if (!hpFilter.value || !targetHP.value || targetHP.value <= 0) {
      return baseVars;
    }

    // For HPS motors, the minimum available is typically 300 HP
    // If required HP is very low (< 100), show motors up to 500 HP instead of using strict range
    // This prevents showing "no motors" when the required HP is below minimum available
    const isLowHP = targetHP.value < 100;

    let minHP: number;
    let maxHP: number;

    if (isLowHP) {
      // Show smallest available motors (up to 500 HP) when required HP is very low
      minHP = 0;
      maxHP = 500;
    } else {
      // Normal case: only show motors that meet or exceed required HP (no underpowered motors)
      // Max at 25% over to avoid showing grossly oversized motors
      minHP = targetHP.value;
      maxHP = Math.ceil(targetHP.value * 1.25);
    }

    const hpCondition: Motors_Bool_Exp = {
      nameplate_horsepower: {
        _gte: minHP,
        _lte: maxHP
      }
    };

    if (!baseVars.where) {
      return {
        ...baseVars,
        where: hpCondition
      };
    }

    return {
      ...baseVars,
      where: {
        _and: [baseVars.where, hpCondition]
      }
    };
  };

  const queryVariables = computed(() => getVariables());
  const {
    data,
    executeQuery: refetch,
    stale: isStale,
    fetching
  } = useHpsMotorsQuery({
    variables: queryVariables
  });

  const motors = computed(() => data.value?.motors as Motors[] | undefined);

  const allRows: ComputedRef<HpsMotorRowData[]> = computed(() => {
    const rows =
      motors.value?.map((m: Motors) => {
        return {
          ...m,
          nameplate_horsepower_50: get50HzNameplate(m.nameplate_horsepower),
          nameplate_voltage_50: get50HzNameplate(m.nameplate_voltage)
        };
      }) || [];

    if (hpFilter.value && targetHP.value && rows.length) {
      const target = targetHP.value;

      // Always sort by closest to target HP (nameplate_horsepower)
      // This ensures best motor recommendations based on HP requirements
      const sorted = [...rows].sort((a, b) => {
        const aHp = a.nameplate_horsepower ?? 0;
        const bHp = b.nameplate_horsepower ?? 0;
        const aDiff = Math.abs(aHp - target);
        const bDiff = Math.abs(bHp - target);
        return aDiff - bDiff;
      });
      return sorted;
    }

    return rows;
  });

  const hashOrder: (keyof Motors)[] = [
    "manufacturer",
    "part_number",
    "nameplate_horsepower",
    "nameplate_voltage",
    "nameplate_current",
    "outer_diameter_inches"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertMotor } = useInsertMotorMutation();
  const { executeMutation: executeUpdateMotor } = useUpdateMotorByPkMutation();
  const { executeMutation: executeDeleteMotor } = useDeleteMotorsMutation();
  const { executeMutation: executeRestoreMotor } = useRestoreMotorsMutation();

  const insertMotor = (
    motorRowData: Omit<HpsMotorRowData, "sizing_import" | "user">
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { nameplate_horsepower_50, nameplate_voltage_50, ...motor } =
      motorRowData;
    return executeInsertMotor({ object: motor });
  };

  const updateMotor = (id: string, motorRowData: HpsMotorRowData) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { nameplate_horsepower_50, nameplate_voltage_50, ...motor } =
      motorRowData;
    return executeUpdateMotor({ id, object: motor });
  };

  const deleteMotor = (ids: string[]) =>
    executeDeleteMotor({ where: deleteMotors(ids) });

  const restoreMotor = (ids: string[]) =>
    executeRestoreMotor({ where: restoreMotors(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "motors",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    allow_rerate: true,
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: "",
    part_number: "",
    outer_diameter_inches: null,
    inner_diameter_inches: null,
    neck_outer_diameter_inches: null,
    neck_inner_diameter_inches: null,
    length_inches: null,
    weight_pounds: null,
    mle_offset_depth_inches: 0,
    outer_diameter_rub_inches: 0,
    nameplate_horsepower: null,
    nameplate_horsepower_50: null,
    nameplate_voltage: null,
    nameplate_voltage_50: null,
    nameplate_current: null,
    poles: null,
    configuration: "",
    shaft_horsepower: null,
    shaft_height_inches: null,
    temperature_rating_fahrenheit: null,
    oil_volume_gallons: null,
    nb: null,
    n1: null,
    n2: null,
    n3: null,
    n4: null,
    n5: null,
    ib: null,
    i1: null,
    i2: null,
    i3: null,
    i4: null,
    i5: null,
    pfb: null,
    pf1: null,
    pf2: null,
    pf3: null,
    pf4: null,
    pf5: null,
    effb: null,
    eff1: null,
    eff2: null,
    eff3: null,
    eff4: null,
    eff5: null,
    nv0: 1,
    nv1: 0,
    nv2: 0,
    iv0: 1,
    iv1: 0,
    iv2: 0,
    pfv0: 1,
    pfv1: 0,
    pfv2: 0,
    effv0: 1,
    effv1: 0,
    effv2: 0,
    tv0: 1,
    tv1: 0,
    tv2: 0,
    rr_vb: null,
    rr_v1: null,
    rr_v2: 0,
    rr_v3: 0,
    rr_lb: null,
    rr_l1: null,
    rr_l2: null,
    is_hps: true
  }));

  return {
    searchPlaceholder: "Search HPS Motors...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "375 DMP"`,
    columns: defineVisibleColumns(HpsMotorTableColumns),
    handleUpsert: defineUpsert(insertMotor, updateMotor),
    handleDelete: defineDelete(deleteMotor),
    handleRestore: defineRestore(restoreMotor),
    totalCount: computed(() => data.value?.motors_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    fetching,
    ...baseEquipment,
    ...defineFilterData({
      columns: HpsMotorTableColumns,
      globalSearchFields: [
        "manufacturer",
        "nameplate_horsepower",
        "part_number"
      ],
      allRows,
      title
    }),
    hpFilter,
    targetHP
  };
}
